import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map, takeUntil } from 'rxjs';
import { GetLoyaltyActiveCollectionsResponseDto } from '@src/api';
import { BaseResizableComponent } from '@src/app/modules/base';

import { LoyaltyProgramCategory } from '../models';
import { CategoriesStoreKeys, LoyaltyProgramPermService, LpCategoriesStoreService } from '../services';
import { GroupItem, LoyaltyProgramFilterData, SelectItem } from '../types';
import { NEW_CATEGORY_ITEM } from '../constants';
import {
  LoyaltyProgramCollectionsService,
  CollectionsStoreKeys,
  LpActiveCollectionsStoreService,
} from '../lp-collections';

@Component({
  selector: 'lp-carousel-categories',
  templateUrl: './lp-carousel-categories.component.html',
  styleUrls: ['./lp-carousel-categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpCarouselCategoriesComponent extends BaseResizableComponent implements OnChanges {
  private readonly route = inject(ActivatedRoute);
  private readonly lpActiveCollectionsStore = inject(LpActiveCollectionsStoreService);
  private readonly loyaltyProgramCollectionsService = inject(LoyaltyProgramCollectionsService);
  private readonly lpCategoriesStore = inject(LpCategoriesStoreService);
  private readonly permService = inject(LoyaltyProgramPermService);

  @Input() filter?: LoyaltyProgramFilterData;

  @Output() applyFilter = new EventEmitter<LoyaltyProgramFilterData>();
  @Output() applyFilterAndChangeRoute = new EventEmitter<LoyaltyProgramFilterData>();

  activeCollections?: LoyaltyProgramCategory[];

  selectedCell?: LoyaltyProgramCategory;
  selectedMainCategory?: LoyaltyProgramCategory;

  readonly newCategoryItem = NEW_CATEGORY_ITEM;
  readonly allowActiveCollectionsViewing = this.permService.allowActiveCollectionsViewing;

  categories?: ReadonlyArray<GroupItem>;

  override ngOnInit(): void {
    super.ngOnInit();

    this.loyaltyProgramCollectionsService.loadActiveCollections();

    this.lpActiveCollectionsStore
      .getDataObservable(CollectionsStoreKeys.Active)
      .pipe(
        takeUntil(this.destroyed$$),
        map(collections => this.mapCollectionsToCategories(collections)),
      )
      .subscribe(activeCollections => {
        this.activeCollections = activeCollections ?? [];
        this.cdr.markForCheck();
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(queryParams => {
        const categoryIds = queryParams.getAll('categoryIds');
        this.lpCategoriesStore
          .getDataObservable(CategoriesStoreKeys.InUse)
          .pipe(takeUntil(this.destroyed$$))
          .subscribe(groupItems => {
            this.categories = groupItems;

            if (groupItems) {
              const categories: SelectItem[] = [];
              groupItems.forEach(groupItem => {
                if (categoryIds.includes(groupItem.id)) {
                  categories.push(...groupItem.items);
                } else {
                  categories.push(...groupItem.items.filter(item => categoryIds.includes(item.id)));
                }
              });

              const initFilter: LoyaltyProgramFilterData = {
                categories,
              };

              this.setSelectedCategoriesByFilter(initFilter);

              this.applyFilter.emit(initFilter);
            }

            this.cdr.markForCheck();
          });
      })
      .unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter && this.filter) {
      this.setSelectedCategoriesByFilter(this.filter);
    }
  }

  onSelectMainCategory(category: LoyaltyProgramCategory) {
    this.selectedCell = category;
    this.selectedMainCategory = category;
    this.applyFilterAndChangeRoute.emit({
      categories: category.items,
    });
  }

  onSelectCategory(category: LoyaltyProgramCategory) {
    this.selectedCell = category;
    this.applyFilterAndChangeRoute.emit({
      categories: category.items ?? [category],
    });
  }

  onResetCategory() {
    this.selectedCell = undefined;
    this.selectedMainCategory = undefined;
    this.applyFilterAndChangeRoute.emit({
      categories: [],
    });
  }

  private setSelectedCategoriesByFilter(filter: LoyaltyProgramFilterData) {
    const { categories } = filter;

    if (!categories?.length) {
      this.selectedCell = undefined;
      this.selectedMainCategory = undefined;
      return;
    }

    this.selectedMainCategory = this.categories?.find(
      mainCategory =>
        !mainCategory.items.filter(
          mainCategoryItem => !categories?.some(category => mainCategoryItem.id === category.id),
        ).length,
    );

    if (this.selectedMainCategory) {
      this.selectedCell = this.selectedMainCategory;
    }

    if (!this.selectedMainCategory && categories?.length === 1) {
      this.selectedCell = categories[0];
      this.selectedMainCategory = this.categories?.find(
        mainCategory => !!mainCategory.items.find(mainCategoryItem => this.selectedCell?.id === mainCategoryItem.id),
      );
    }
  }

  private mapCollectionsToCategories(
    collections?: GetLoyaltyActiveCollectionsResponseDto[] | null,
  ): LoyaltyProgramCategory[] | null {
    if (!collections) {
      return null;
    }

    const categories: LoyaltyProgramCategory[] = [];
    collections.forEach(collection => {
      categories.push({
        id: collection.id,
        name: collection.bannerTitle ?? '',
        imageId: collection.bannerBackgroundPhotoId,
        unionId: collection.parentOrganisationId,
        link: `collections/${collection.id}`,
      });
    });
    return categories;
  }
}
